import { useState, useEffect, useCallback } from 'react';
import { BrowserProvider, ethers, Eip1193Provider } from 'ethers';
import { accountThunks } from '~/slices';
import { useDispatch } from './useDispatch';
import { useSelector } from './useSelector';
import {
  WALLET_STORAGE_KEY,
  BSC_EVM_MAINNET_CHAIN_ID_HEX,
  BSC_EVM_MAINNET_CHAIN_CONFIG,
} from '~/constants';

export type Eip6963ProviderDetail = {
  info: {
    name: string;
    icon: string;
    rdns?: string;
  };
  provider: Eip1193Provider;
};

export function useWalletConnector() {
  const dispatch = useDispatch();
  const { isAuth, wallet_address, loading } = useSelector(
    (state) => state.account,
  );
  const [providers, setProviders] = useState<Eip6963ProviderDetail[]>([]);
  const [selectedDetail, setSelectedDetail] =
    useState<Eip6963ProviderDetail | null>(null);
  const [browserProvider, setBrowserProvider] =
    useState<BrowserProvider | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const discovered: Eip6963ProviderDetail[] = [];

    const onAnnounce = (e: any) => {
      const detail = e.detail as Eip6963ProviderDetail;
      if (
        detail?.info?.rdns &&
        !discovered.some((p) => p.info.rdns === detail.info.rdns)
      ) {
        discovered.push(detail);
        setProviders([...discovered]);
      }
    };

    window.addEventListener('eip6963:announceProvider', onAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    return () => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce);
    };
  }, []);

  const connectWallet = useCallback(
    async (detail: Eip6963ProviderDetail) => {
      setIsConnecting(true);
      setError(null);

      try {
        const provider = new BrowserProvider(detail.provider, 'any');
        const accounts = await provider.send('eth_requestAccounts', []);

        if (!accounts || accounts.length === 0) {
          throw new Error('The user did not grant access to the account');
        }

        try {
          await provider.send('wallet_switchEthereumChain', [
            { chainId: BSC_EVM_MAINNET_CHAIN_ID_HEX },
          ]);
        } catch {
          try {
            await provider.send('wallet_addEthereumChain', [
              BSC_EVM_MAINNET_CHAIN_CONFIG,
            ]);

            await provider.send('wallet_switchEthereumChain', [
              { chainId: BSC_EVM_MAINNET_CHAIN_ID_HEX },
            ]);
          } catch (addError) {
            console.error('Failed to add/switch to BSC network:', addError);
            throw new Error("Couldn't switch network to BSC");
          }
        }

        const signer = await provider.getSigner();
        const addr = await signer.getAddress();
        const network = await provider.getNetwork();

        // Если мы уже залогинены с этим адресом, пропускаем подпись
        if (
          isAuth &&
          wallet_address &&
          wallet_address.toLowerCase() === addr.toLowerCase()
        ) {
          console.log('Already logged in, skipping signature');
          setSelectedDetail(detail);
          setBrowserProvider(provider);
          setAddress(addr);
          setChainId(Number(network.chainId));
          if (detail.info.rdns) {
            localStorage.setItem(WALLET_STORAGE_KEY, detail.info.rdns);
          }
          setIsConnecting(false);
          return;
        }

        const message =
          'I confirm ownership of this wallet.\n\n' +
          `Address: ${addr}\n\n` +
          `Time: ${new Date().toISOString()}\n\n` +
          'Do NOT sign this message on other websites!';

        let signature: string;
        try {
          signature = await signer.signMessage(message);
        } catch (signError) {
          console.error('Signing failed:', signError);
          throw new Error(
            'The signature of the message was canceled or failed',
          );
        }

        // Логин
        await dispatch(
          accountThunks.login({
            message,
            signature: Array.from(ethers.getBytes(signature)),
            walletAddress: addr,
          }),
        );

        setSelectedDetail(detail);
        setBrowserProvider(provider);
        setAddress(addr);
        setChainId(Number(network.chainId));

        if (detail.info.rdns) {
          localStorage.setItem(WALLET_STORAGE_KEY, detail.info.rdns);
        }
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Couldn't connect to wallet");
        setBrowserProvider(null);
        setAddress(null);
        setChainId(null);
      } finally {
        setIsConnecting(false);
      }
    },
    [dispatch, isAuth, wallet_address],
  );

  useEffect(() => {
    const savedRdns = localStorage.getItem(WALLET_STORAGE_KEY);
    if (!savedRdns || providers.length === 0 || loading) {
      return;
    }

    const match = providers.find(
      (provider) => provider.info.rdns === savedRdns,
    );
    if (match) {
      connectWallet(match);
    }
  }, [providers, loading, connectWallet]);

  const disconnect = useCallback(() => {
    localStorage.removeItem(WALLET_STORAGE_KEY);
    setSelectedDetail(null);
    setBrowserProvider(null);
    setAddress(null);
    setChainId(null);
    setError(null);
  }, []);

  return {
    // State
    providers,
    selectedDetail,
    provider: browserProvider,
    address,
    chainId,
    isConnecting,
    error,

    // Actions
    connectWallet,
    disconnect,

    // Getters
    isConnected: !!address,
    selectedWalletName: selectedDetail?.info.name || null,
  };
}

// ---------------------------------------------------------------------------

export function getContract(
  address: string,
  abi: ethers.InterfaceAbi,
  providerOrSigner: ethers.Provider | ethers.Signer,
): ethers.Contract {
  if (!providerOrSigner) {
    throw new Error('Provider or Signer not passed');
  }
  return new ethers.Contract(address, abi, providerOrSigner);
}
